// Client half of F8's exactly-once guarantee. Mutations that fail because the network
// is unavailable are persisted here and replayed when connectivity returns. Every entry
// carries a client-generated Idempotency-Key that travels on the original attempt and on
// every replay, so the server's idempotency middleware collapses duplicates: a queued
// mutation applies exactly once no matter how many times we resend it.
//
// Persistence is localStorage so the queue survives a reload or a tab crash while the
// user is offline. Replays run sequentially in FIFO order.
import { API_PREFIX } from '@alignova/shared';

export interface QueuedMutation {
  id: string; // === Idempotency-Key
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  createdAt: string;
}

export interface QueueStatus {
  pending: number;
  syncing: boolean;
}

const STORAGE_KEY = 'dentallab.offlineQueue.v1';

const queue: QueuedMutation[] = load();
let syncing = false;
const listeners = new Set<(s: QueueStatus) => void>();

function load(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as QueuedMutation[]) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Storage full or unavailable (private mode) — the in-memory queue still works
    // for the current session; nothing more we can safely do here.
  }
}

function emit(): void {
  const snapshot: QueueStatus = { pending: queue.length, syncing };
  for (const cb of listeners) cb(snapshot);
}

export function queueStatus(): QueueStatus {
  return { pending: queue.length, syncing };
}

export function onQueueChange(cb: (s: QueueStatus) => void): () => void {
  listeners.add(cb);
  cb(queueStatus());
  return () => listeners.delete(cb);
}

/** A network error means "unreachable", not "rejected" — only these are worth queuing. */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError; // fetch() rejects with TypeError on connection failure
}

/** Mint an Idempotency-Key. Callers attach it to the first online attempt too, so a
 *  response lost after the server applied the write can't cause a double-apply on
 *  replay — the same key dedups both. */
export function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Deterministic-enough fallback for ancient runtimes; collisions are astronomically
  // unlikely across a single client's pending set.
  return `k-${Date.now().toString(36)}-${performance.now().toString(36).replace('.', '')}`;
}

/** Add a mutation to the durable queue and return its Idempotency-Key. Pass an existing
 *  `id` to reuse the key already sent on a failed online attempt. */
export function enqueue(mutation: Omit<QueuedMutation, 'id' | 'createdAt'> & { id?: string }): string {
  const id = mutation.id ?? newKey();
  queue.push({ method: mutation.method, path: mutation.path, body: mutation.body, id, createdAt: new Date().toISOString() });
  persist();
  emit();
  return id;
}

async function send(m: QueuedMutation): Promise<void> {
  const res = await fetch(`${API_PREFIX}${m.path}`, {
    method: m.method,
    credentials: 'include',
    headers: {
      'Idempotency-Key': m.id,
      ...(m.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: m.body !== undefined ? JSON.stringify(m.body) : undefined,
  });
  if (!res.ok && res.status >= 500) {
    // Server-side transient failure — throw so we keep the entry and retry later.
    throw new Error(`replay failed: ${res.status}`);
  }
  // 2xx (including the server's { duplicate:true } short-circuit) and 4xx are terminal:
  // a 4xx will never succeed on retry, so we drop it rather than loop forever.
}

/** Replay every queued mutation in order. Stops early and keeps the tail on a network
 *  drop so ordering is preserved; drops entries the server terminally rejects. */
export async function flushQueue(): Promise<void> {
  if (syncing || queue.length === 0) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  syncing = true;
  emit();
  try {
    while (queue.length > 0) {
      const next = queue[0]!;
      try {
        await send(next);
        queue.shift(); // succeeded or terminally rejected - remove
      } catch (err) {
        if (isNetworkError(err)) return; // still offline; leave the queue intact
        queue.shift(); // 5xx after our own throw: drop to avoid a poison-message loop
      } finally {
        persist();
        emit();
      }
    }
  } finally {
    syncing = false;
    emit();
  }
}

let started = false;
/** Install the triggers that drain the queue: on regained connectivity and on a timer
 *  as a safety net. Idempotent — safe to call more than once. */
export function startOfflineQueue(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => void flushQueue());
  setInterval(() => void flushQueue(), 15_000);
  void flushQueue(); // drain anything left over from a previous session
}
